<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";

import { linkActions } from "@/action/linkActions";
import { navigationActions } from "@/action/navigationActions";
import { objectiveActions } from "@/action/objectiveActions";
import { objectiveMarkedAs } from "@/analytics/events/other";
import { trackEvent } from "@/analytics/track";
import ExpandableTextarea from "@/components/input/ExpandableTextarea.vue";
import MaxLengthTextarea from "@/components/input/MaxLengthTextarea.vue";
import LinkDragHandle from "@/components/linkDrag/LinkDragHandle.vue";
import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { isLinkDragging, useLinkDragSource } from "@/composables/linkDrag";
import { boardTypeToLevel } from "@/model/baseTypes";
import type { BoardWithObjectives } from "@/model/board";
import { normalLinkColors } from "@/model/colors";
import type { LinkingTarget } from "@/model/link";
import type { Objective, ObjectiveType } from "@/model/objective";
import { useArtObjectivesPanelStore } from "@/store/artObjectivesPanel";
import { useUserStore } from "@/store/user";
import { useWorkModeStore } from "@/store/workMode";
import { isFeatureEnabled } from "@/utils/env/feature";

import ObjectiveCardAttributes from "./ObjectiveCardAttributes.vue";
import SetObjectivePoints from "./SetObjectivePoints.vue";

const props = defineProps<{
  objective: Objective;
  type: ObjectiveType;
  index: number;
  board: BoardWithObjectives;
  isDragging: boolean;
  expandDescriptions: boolean;
  hasStatusDistribution: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  pinned?: boolean;
  clickable?: boolean; // Determines which cursor is shown
  enableLinkDrag?: boolean;
}>();

const panelStore = useArtObjectivesPanelStore();
const route = useRoute();
const isExplorerEnabled = computed(() => isFeatureEnabled(route, "explorer"));
let priorPin: string | null = null;

const { handleProps: linkDragHandleProps } = useLinkDragSource(
  () => ({ kind: "objective", boardId: props.board.id, id: props.objective.id }),
  {
    color: normalLinkColors.team,
    // Pin the source during drag so linked team rows stay visible while
    // unlinked ones dim (via the existing ART-panel dim mechanism).
    onStart: (source) => {
      priorPin = panelStore.pinnedArtObjectiveId;
      panelStore.setPin(source.id);
    },
    onDragEnd: () => panelStore.setPin(priorPin),
    onDrop: (from, to) =>
      linkActions.addObjectiveToObjective("drag", from.boardId, from.id, to.boardId, to.id),
  },
);

const emit = defineEmits<{ removed: [] }>();

const isFocused = ref(false);

watch(
  () => props.isDragging,
  () => setIsFocused(false),
);

const isExecutionMode = computed(() => useWorkModeStore().isExecutionMode);
const readonly = computed(() => !useUserStore().isAllowed("edit"));

// The title swaps between a content-hugging read element and the editable
// textarea (mirrors the description's summary/textarea pattern) so only the
// visible text is clickable. Show the input while editing, or when empty so
// the placeholder is reachable. `titleEditing` is latched on focus, not just on
// click, so typing the first character into an empty title doesn't make the
// text non-empty and unmount the focused textarea from under the caret.
const titleEditing = ref(false);
const titleInput = useTemplateRef("titleInput");
const showTitleInput = computed(
  () => !readonly.value && (titleEditing.value || props.objective.text.trim() === ""),
);
// Bind a blank string for a whitespace-only title so the native "Title"
// placeholder shows instead of invisible whitespace.
const titleValue = computed(() => (props.objective.text.trim() === "" ? "" : props.objective.text));

async function startTitleEdit() {
  if (readonly.value) return;
  titleEditing.value = true;
  await nextTick();
  titleInput.value?.focus();
}

function stopTitleEdit() {
  titleEditing.value = false;
}

function setTitle(title: string) {
  objectiveActions.setTitle("modal", props.board.id, props.objective, title);
}

function setDescription(description: string) {
  objectiveActions.setDescription("modal", props.board.id, props.objective, description);
}

function setIsFocused(focused: boolean) {
  isFocused.value = focused;
}

function setBusinessValue(value: number) {
  objectiveActions.setBv(
    "modal",
    props.board.id,
    props.objective,
    props.type === "committed",
    value,
  );
}

function setActualValue(value: number) {
  objectiveActions.setAv(
    "modal",
    props.board.id,
    props.objective,
    props.type === "committed",
    value,
  );
}

async function markAsUncommitted() {
  objectiveActions.move("modal", props.board.id, props.objective.id, {
    stretch: true,
    rank: props.board.stretchObjectives.length,
  });

  trackEvent(objectiveMarkedAs(props.objective.id, true, boardTypeToLevel(props.board.type)));

  // move focus to the objective after it's rerendered in the new state
  await nextTick();
  document.getElementById(`objective-card-${props.objective.id}`)?.focus();
}

async function markAsCommitted() {
  objectiveActions.move("modal", props.board.id, props.objective.id, {
    stretch: false,
    rank: props.board.objectives.length,
  });

  trackEvent(objectiveMarkedAs(props.objective.id, false, boardTypeToLevel(props.board.type)));

  // move focus to the objective after it's rerendered in the new state
  await nextTick();
  document.getElementById(`objective-card-${props.objective.id}`)?.focus();
}

function remove() {
  objectiveActions.remove("modal", props.board.id, props.objective.id);

  // Notify the parent component that the objective has been removed
  emit("removed");
}

/**
 * Move the objective up in the list
 */
function moveUp() {
  objectiveActions.move("context-menu", props.board.id, props.objective.id, {
    stretch: props.type === "uncommitted",
    rank: props.index - 2, // (local index is 1-based, API index is 0-based)
  });
}

/**
 * Move the objective down in the list
 */
function moveDown() {
  objectiveActions.move("context-menu", props.board.id, props.objective.id, {
    stretch: props.type === "uncommitted",
    rank: props.index, // (local index is 1-based, API index is 0-based)
  });
}

function showBreakdown() {
  navigationActions.showBreakdown("art-objectives-board", linkingTarget());
}

function showMap() {
  navigationActions.showMap("art-objectives-board", linkingTarget());
}

function linkingTarget(): LinkingTarget {
  return { type: "objective", id: props.objective.id, boardId: props.board.id };
}
</script>

<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events vuejs-accessibility/no-static-element-interactions -->
<template>
  <article
    :id="`objective-card-${objective.id}`"
    class="objective-card"
    :class="{ pinned }"
    tabindex="-1"
    :data-testid="`objective-card-${objective.id}`"
    :data-id="objective.id"
    :aria-label="$t('label.objective', { text: objective.text, index })"
    @mouseenter="setIsFocused(true)"
    @mouseleave="setIsFocused(false)"
  >
    <!-- eslint-enable vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
    <div
      class="objective-inner-container"
      :class="{
        focused: isFocused && !isDragging,
        dragging: isDragging,
        pinned,
        clickable,
        draggable: !readonly,
      }"
    >
      <div class="objective-leading">
        <span class="objective-index">{{ index }}</span>
        <SvgIcon
          v-if="isFocused && !isDragging && !readonly"
          class="objective-drag-icon"
          name="arrow/up-down"
          width="20"
          height="20"
        />
      </div>
      <div class="objective-title">
        <MaxLengthTextarea
          v-if="showTitleInput"
          ref="titleInput"
          spellcheck="false"
          :placeholder="$t('objectives.title.placeholder')"
          :model-value="titleValue"
          :maxlength="260"
          :readonly
          bordered
          @update:model-value="setTitle"
          @focus="titleEditing = true"
          @blur="stopTitleEdit"
        />
        <button v-else-if="!readonly" class="title-summary" @click.stop="startTitleEdit">
          {{ objective.text }}
        </button>
        <div v-else class="title-readonly">{{ objective.text }}</div>
        <div class="objective-title-actions">
          <IconButton
            icon="board/breakdown"
            :text="$t('breakdownModal.title')"
            @click="showBreakdown"
          />
          <IconButton
            v-if="isExplorerEnabled"
            icon="board/map"
            :text="$t('mapModal.title')"
            highlight="map"
            @click="showMap"
          />
          <DropdownMenu v-if="!readonly">
            <template #trigger="{ isOpen }">
              <IconButton
                data-dropdown-trigger
                icon="system/more-horizontal"
                :aria-label="$t('label.objective.menu')"
                :aria-expanded="isOpen"
                aria-controls="objective-card-dropdown-menu"
                aria-haspopup="menu"
              />
            </template>
            <MenuList id="objective-card-dropdown-menu" role="menu">
              <MenuListItems>
                <MenuListItem v-if="type === 'committed'" @click="markAsUncommitted">
                  <template #before>
                    <SvgIcon name="general/uncommitted" width="20" height="20" />
                  </template>
                  {{ $t("objective.markAsUncommitted") }}
                </MenuListItem>
                <MenuListItem v-if="type === 'uncommitted'" @click="markAsCommitted">
                  <template #before>
                    <SvgIcon name="general/committed" width="20" height="20" />
                  </template>
                  {{ $t("objective.markAsCommitted") }}
                </MenuListItem>
                <MenuListItem v-if="canMoveUp" @click="moveUp">
                  <template #before>
                    <SvgIcon name="arrow/chevron-up" width="20" height="20" />
                  </template>
                  {{ $t("objective.moveUp") }}
                </MenuListItem>
                <MenuListItem v-if="canMoveDown" @click="moveDown">
                  <template #before>
                    <SvgIcon name="arrow/chevron-down" width="20" height="20" />
                  </template>
                  {{ $t("objective.moveDown") }}
                </MenuListItem>
                <MenuListItem @click="remove">
                  <template #before>
                    <SvgIcon name="general/delete" width="20" height="20" />
                  </template>
                  {{ $t("general.delete") }}
                </MenuListItem>
              </MenuListItems>
            </MenuList>
          </DropdownMenu>
        </div>
      </div>
      <div class="objective-description">
        <ExpandableTextarea
          :expand-description="expandDescriptions"
          :model-value="objective.description"
          spellcheck="false"
          :readonly
          @update:model-value="setDescription"
        />
      </div>

      <div class="objective-details">
        <div class="objective-values">
          <SetObjectivePoints
            :current="objective.bv"
            :readonly
            :aria-label="$t('label.objective.bvButton')"
            @select="setBusinessValue"
          />
          <div class="objective-value-title">
            <span aria-hidden="true">
              <BaseTooltip v-if="isExecutionMode">
                {{ $t("objectives.bv") }}
                <template #content>
                  {{ $t("objectives.businessValue") }}
                </template>
              </BaseTooltip>
              <div v-else>{{ $t("objectives.businessValue") }}</div>
            </span>
            <div class="screen-reader-only">
              {{ $t("label.objective.bv", { value: objective.bv }) }}
            </div>
          </div>
          <SetObjectivePoints
            v-if="isExecutionMode"
            :current="objective.av"
            :readonly
            :aria-label="$t('label.objective.avButton')"
            @select="setActualValue"
          />
          <div v-if="isExecutionMode" class="objective-value-title actual-value">
            <span aria-hidden="true">
              <BaseTooltip>
                {{ $t("objectives.av") }}
                <template #content>
                  {{ $t("objectives.actualValue") }}
                </template>
              </BaseTooltip>
            </span>
            <div class="screen-reader-only">
              {{ $t("label.objective.av", { value: objective.av }) }}
            </div>
          </div>
        </div>
        <MenuItemDelimiter />
        <ObjectiveCardAttributes :objective :board :has-status-distribution />
      </div>
    </div>
    <LinkDragHandle
      v-if="enableLinkDrag && !readonly && !isDragging && !isLinkDragging"
      v-bind="linkDragHandleProps"
      class="link-drag-position"
    />
  </article>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/utils";
@use "@/styles/variables";
@use "@/styles/variables/colors";
@use "@/styles/mixins/a11y";
@use "@/styles/mixins/typography";
@use "@/styles/z-index";

.objective-card {
  position: relative;
  margin-bottom: 24px;
  background-color: colors.$background-ui;
  border-radius: variables.$objective-card-border-radius;

  // Elevate hovered/pinned cards so the dangling handle sits above siblings
  // and stays hit-testable regardless of paint order in the sortable list.
  &:hover,
  &.pinned {
    z-index: z-index.$low;
  }

  .link-drag-position {
    position: absolute;
    left: 50%;
    bottom: -20px;
    transform: translateX(-50%);
    width: 12px;
    height: 20px;
    opacity: 0;
    transition: opacity 120ms ease;
  }

  &:hover .link-drag-position,
  &.pinned .link-drag-position {
    opacity: 1;
  }

  // Hide the handle before the browser's HTML5 drag-image snapshot — the
  // Vue `isDragging` prop update lands too late.
  &.sortable-chosen .link-drag-position,
  &.objective-drag-state .link-drag-position {
    display: none;
  }

  // The card lays out as a 2-column grid:
  //   col 1 = leading slot (index / drag icon)
  //   col 2 = content (title row, description, details)
  // Title, description, and details all sit in col 2, so they align by
  // structure — no padding math.
  .objective-inner-container {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 8px;
    align-items: center;
    border: 1px solid colors.$border-subtle;
    padding: 10px 16px 16px;
    border-radius: variables.$objective-card-border-radius;

    &.focused {
      border: 1px solid colors.$border;
    }

    // Reflect the available interaction: `grab` while the card is draggable and
    // `pointer` when it's clickable
    &.focused.draggable {
      cursor: grab;
    }

    &.focused.clickable {
      cursor: pointer;
    }

    &.dragging {
      cursor: grabbing;
    }

    // `.pinned` wins over `.focused`: when the user has clicked this card to
    // highlight links, keep the strong border even on hover.
    &.pinned {
      border: 1px solid colors.$text;
    }
  }

  // Leading slot: index sets the slot's width, drag icon overlays absolutely
  // on hover. Only occupies the title row of the grid.
  .objective-leading {
    grid-column: 1;
    grid-row: 1;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 12px;
  }

  // Always show a grab cursor over the drag icon to suggest to users that they can drag the card
  // even if the rest of the card shows a pointer
  .draggable .objective-leading {
    cursor: grab;
  }

  .objective-index {
    @include typography.medium("semi-bold");

    color: colors.$text-subtle;
  }

  .objective-drag-icon {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background-color: colors.$background-ui;
  }

  .objective-title {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    align-items: center;
    min-width: 0;
  }

  // Read-mode title: hugs its text so only the visible text is the click
  // target (not the row's trailing whitespace). Padding/border/typography
  // mirror the textarea so swapping to edit mode doesn't shift the text.
  .title-summary,
  .title-readonly {
    @include typography.medium("semi-bold");

    min-width: 0;
    max-width: 100%;
    padding: 0.5rem;
    border: 1px solid transparent;
    color: colors.$text;
    text-align: left;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }

  .title-summary {
    font-family: inherit;
    background: none;
    outline: revert;
    cursor: text;

    // Reserve space for the character count, so text doesn't shift on edit
    margin-right: variables.$textarea-count-gutter;
  }

  // Keep the breakdown/menu actions at the row's right edge now that the
  // read-mode title no longer stretches to fill the row.
  .objective-title-actions {
    display: flex;
    align-items: center;
    margin-left: auto;
  }

  .objective-description,
  .objective-details {
    grid-column: 2;
    min-width: 0;
  }

  .objective-description {
    margin-bottom: 0.75rem;
  }

  .objective-details {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 8px;
  }

  .objective-values {
    display: flex;
    align-items: center;
    gap: 8px;

    .objective-value-title {
      @include typography.small("medium");
    }
  }

  .list-item > * {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  &.objective-drag-state > .objective-inner-container {
    visibility: hidden;
  }

  .screen-reader-only {
    @include a11y.screen-reader-only;
  }
}
</style>

<style lang="scss">
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.objective-card {
  .status-distribution {
    --distribution-border-width: 2px;
  }

  .objective-title .base-textarea {
    @include typography.medium("semi-bold");
  }

  .objective-description {
    .description-textarea {
      @include typography.medium("medium");

      position: static;
      color: colors.$text;
    }

    .description-summary {
      @include typography.medium("medium");
    }
  }
}

.objective-menu-item {
  padding: 0;
}
</style>
